/**
 * tyt-faq-filter.js
 *
 * Client-side search for /pages/faq (52 questions across 8 sections).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT A SUBSTRING MATCH (rewritten 2026-09-04)
 *
 * The first version required EVERY word typed to appear somewhere in the
 * question or the answer. That is fine for a trail name and wrong for a
 * question: Maud typed "How long does it take" on her phone and got nothing
 * back, even though the question she wanted ("How long until my poster
 * arrives?") was sitting right there. A customer reads that as broken.
 *
 * Narrowing one word at a time over the real 52 questions shows exactly how it
 * died:  52 → 24 ("how") → 5 ("long") → 0 ("does").  The instructive part is
 * that "does" is NOT a rare word here — six items contain it — but none of
 * those six also contains "long". One ordinary word landing in the wrong six
 * items is all it takes for an AND search to empty the page.
 *
 * People type questions, not keywords, so the matcher has to ignore the
 * scaffolding words and rank on the words that carry meaning.
 *
 * THE RULE, in one sentence: drop the filler words, then show the questions
 * that match the MOST of what is left.
 *
 * That is a progressive relaxation, and it is why it behaves well at both ends:
 *   - "poster damaged"        → items matching BOTH win the top tier, so the
 *                               result is as tight as the old AND search.
 *   - "How long does it take" → "how/does/it" are dropped; "long" and "take"
 *                               remain; items matching both come top; if none
 *                               match both, the ones matching either are shown
 *                               instead of nothing.
 * Only the top tier renders. Widening to every partial match would put 30 of
 * 52 questions on screen for a query like "poster", which is no more useful
 * than zero.
 *
 * WORD MATCHING IS DELIBERATELY THE SAME AS THE TRAIL SEARCH — SEP, startsWith
 * and splitWords are copied from assets/tyt-collection-filter.js on purpose.
 * Two searches on one site that disagree about what a word is would be a bug in
 * itself. A term matches the BEGINNING of a word, so "ship" finds "shipping"
 * and "refund" finds "refunded", but "art" does not find "start".
 * Plurals are handled in both directions: the prefix rule covers typing
 * "poster" against "posters", and a trailing "s" is retried singular so typing
 * "posters" also finds "poster".
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Wires up by attribute, no per-page IDs needed:
 *   - [data-tyt-faq-search-input]     the search <input>
 *   - [data-tyt-faq-count]            optional results line under the input
 *   - [data-tyt-faq-section]          each tyt-faq-section <section>
 *   - [data-tyt-faq-item]             each <details> question inside a section
 *   - [data-tyt-faq-q] / [data-tyt-faq-a]  text-bearing elements inside an item
 *   - [data-tyt-faq-empty]            the per-section empty-state placeholder
 *
 * SECTIONS WITH NO MATCH ARE HIDDEN OUTRIGHT WHILE A SEARCH IS ACTIVE
 * (Maud's call, 2026-09-04). When the page was first built they kept their
 * card and showed "No questions match your search in this section", so the
 * layout would not jump under the reader's finger mid-type. With eight
 * sections that traded one problem for a worse one: a two-result search meant
 * scrolling past six empty cards to reach the second answer, which on a phone
 * is most of the page. The search box sits above every section, so what moves
 * is below where the reader is looking.
 *
 * The per-section empty state is left in the Liquid and simply never shown —
 * it costs nothing, and it is what this file falls back to if the hiding here
 * is ever reverted.
 */
(function () {
  'use strict';

  // ── word matching (identical to tyt-collection-filter.js) ──────────────────

  var SEP = /[\s \-‐-―_/\\.,;:!?'"‘’“”()\[\]{}·•|+&@#*~^%$=<>]+/;

  function startsWith(s, p) { return s.lastIndexOf(p, 0) === 0; }

  function splitWords(s) {
    return (s || '').toLowerCase().split(SEP).filter(function (w) { return w.length > 0; });
  }

  // ── the filler words ───────────────────────────────────────────────────────
  /* Question scaffolding only. Nothing here can narrow a search on its own, and
   * nothing that carries meaning on this site is in the list — "long", "take",
   * "print", "ship", "gift", "free" and every product word stay. "when" and
   * "where" go: they open a question, they never answer one. */
  var STOPWORDS = {};
  ('a about after all also am an and any are as at be because been before being ' +
   'but by can cant could did do does doing dont for from get gets getting got ' +
   'had has have having he her here hers him his how i id if im in into is it ' +
   'its ive just like may me might much must my need needs of on once one only ' +
   'or other our out over own please shall she should so some such than that the ' +
   'their them then there these they this those through to too under until up us ' +
   'very was way we were what when where whether which while who whom why will ' +
   'with would you your yours'
  ).split(' ').forEach(function (w) { STOPWORDS[w] = true; });

  /* The words worth searching on. If the reader types nothing but filler
   * ("how do i"), fall back to the raw words rather than matching everything —
   * a live filter that stops reacting looks broken too. */
  function queryTerms(raw) {
    var all = splitWords(raw);
    var kept = all.filter(function (w) { return !STOPWORDS[w]; });
    return kept.length ? kept : all;
  }

  /* One term against one item's words. Prefix match, plus a singular retry so
   * "posters" finds "poster" (the prefix rule already covers the other way). */
  function termHits(words, term) {
    var i;
    for (i = 0; i < words.length; i++) {
      if (startsWith(words[i], term)) return true;
    }
    if (term.length > 3 && term.charAt(term.length - 1) === 's') {
      var singular = term.slice(0, -1);
      for (i = 0; i < words.length; i++) {
        if (startsWith(words[i], singular)) return true;
      }
    }
    return false;
  }

  /* How many of the typed terms this item answers to. This number is the whole
   * ranking — the top scorers are shown and the rest are hidden. */
  function scoreItem(words, terms) {
    var n = 0;
    for (var i = 0; i < terms.length; i++) {
      if (termHits(words, terms[i])) n++;
    }
    return n;
  }

  // ── wiring ─────────────────────────────────────────────────────────────────

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var input = document.querySelector('[data-tyt-faq-search-input]');
    if (!input) return;

    var sections = Array.prototype.slice.call(
      document.querySelectorAll('[data-tyt-faq-section]')
    );
    if (!sections.length) return;

    var countEl = document.querySelector('[data-tyt-faq-count]');
    var COPY = {
      one:  (countEl && countEl.getAttribute('data-count-one'))  || '1 question matches.',
      many: (countEl && countEl.getAttribute('data-count-many')) || '[count] questions match.',
      none: (countEl && countEl.getAttribute('data-count-none')) ||
            'Nothing matches that. Try a single word — shipping, refund, gpx.'
    };

    // Split each item's text into words once; the search runs on every keystroke.
    var index = sections.map(function (section) {
      var items = Array.prototype.slice.call(
        section.querySelectorAll('[data-tyt-faq-item]')
      ).map(function (item) {
        var qNode = item.querySelector('[data-tyt-faq-q]');
        var aNode = item.querySelector('[data-tyt-faq-a]');
        return {
          el: item,
          words: splitWords(
            (qNode ? qNode.textContent : '') + ' ' + (aNode ? aNode.textContent : '')
          )
        };
      });
      return {
        section: section,
        items: items,
        empty: section.querySelector('[data-tyt-faq-empty]')
      };
    });

    function showCount(total, hasQuery) {
      if (!countEl) return;
      if (!hasQuery) { countEl.hidden = true; countEl.textContent = ''; return; }
      countEl.hidden = false;
      countEl.textContent = total === 0 ? COPY.none
        : total === 1 ? COPY.one
        : COPY.many.replace('[count]', total);
    }

    function applyFilter(rawQuery) {
      var terms = queryTerms(rawQuery || '');
      var hasQuery = (rawQuery || '').trim().length > 0 && terms.length > 0;

      if (!hasQuery) {
        index.forEach(function (entry) {
          entry.section.hidden = false;
          entry.items.forEach(function (item) { item.el.hidden = false; });
          if (entry.empty) entry.empty.hidden = true;
        });
        showCount(0, false);
        return;
      }

      // Pass 1 — score everything, and find the best score on the page.
      var best = 0;
      index.forEach(function (entry) {
        entry.items.forEach(function (item) {
          item.score = scoreItem(item.words, terms);
          if (item.score > best) best = item.score;
        });
      });

      // Pass 2 — show only the top tier. best === 0 means nothing matched.
      // A section with nothing in the top tier is hidden whole, so the reader
      // scrolls through matches rather than through empty cards.
      var total = 0;
      index.forEach(function (entry) {
        var visible = 0;
        entry.items.forEach(function (item) {
          var show = best > 0 && item.score === best;
          item.el.hidden = !show;
          if (show) visible++;
        });
        total += visible;
        entry.section.hidden = visible === 0;
        if (entry.empty) entry.empty.hidden = true;
      });

      showCount(total, true);
    }

    // Debounce so we don't thrash on fast typing.
    var t = null;
    input.addEventListener('input', function () {
      clearTimeout(t);
      var value = input.value;
      t = setTimeout(function () { applyFilter(value); }, 60);
    });

    // Clear (X icon on type=search) fires an 'input' event with an empty value,
    // so the handler above already covers it.
  });
})();
